import qs from 'qs';
import { RootState } from '../store';
import { BUSINESS_UNITS, Roles } from '@cambridgeassessment/lib-ee-commons';
import { FEATURE_FLAGS } from 'lib-browser';

export type PossibleUrlParams = 'seriesId' | 'componentId';

export interface Params {
  params: {
    [key in PossibleUrlParams]: string;
  };
}

export interface DynamicRoute {
  title: string;
  path: string;
  dataQaId: string;
  router: boolean;
  size: string;
  availableForBU: string[];
  canAccess: string[];
  selected?: boolean;
  titleResolver?: (state: RootState, match: Params | null) => string | undefined;
  pathResolver?: (state: RootState, match: Params | null) => string | undefined;
  sideNavRoute?: boolean;
  featureFlag?: string;
}

export const routes: DynamicRoute[] = [
  {
    title: 'Series business rules',
    path: '/series',
    dataQaId: 'link-priming-category-series',
    router: true,
    size: 'medium',
    canAccess: [
      Roles.SUBMIT_SUPER_ADMIN,
      Roles.SUBMIT_ADMINISTRATOR,
      Roles.SUBMIT_COMPLIANCE,
      Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
    ],
    sideNavRoute: true,
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Component business rules',
    path: '/components',
    dataQaId: 'link-priming-category-components',
    router: true,
    size: 'medium',
    canAccess: [
      Roles.SUBMIT_SUPER_ADMIN,
      Roles.SUBMIT_ADMINISTRATOR,
      Roles.SUBMIT_COMPLIANCE,
      Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
    ],
    sideNavRoute: true,
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Configure component',
    path: '/components/configure-component/:componentId',
    dataQaId: 'link-priming-category-configure-component',
    router: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR, Roles.SUBMIT_COMPLIANCE],
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
    pathResolver: (_state: RootState, match: Params | null): string | undefined => {
      if (match) {
        return `/components/configure-component/${match.params.componentId}`;
      }
    },
  },
  {
    title: 'Review and confirm component configuration',
    path: '/components/review-configure-component/:componentId',
    dataQaId: 'link-priming-category-configure-component',
    router: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR, Roles.SUBMIT_COMPLIANCE],
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER],
    pathResolver: (_state: RootState, match: Params | null): string | undefined => {
      if (match) {
        return `/components/configure-component/${match.params.componentId}`;
      }
    },
  },
  {
    title: 'Deadlines for the component',
    path: '/components/configure-component/:componentId/deadlines',
    dataQaId: 'link-priming-category-component-deadlines',
    router: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR, Roles.SUBMIT_COMPLIANCE],
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Series malpractice reports',
    path: '/malpracticeReports',
    dataQaId: 'link-priming-category-malpractice-reports',
    router: true,
    sideNavRoute: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER],
    availableForBU: [BUSINESS_UNITS.CIE.id],
  },
  {
    title: 'Series name search results',
    titleResolver: (state: RootState, match: Params | null): string | undefined => {
      if (match) {
        const series = state.malpracticeReport.seriesData?.find(
          (currentSeries) => currentSeries.id === +match.params.seriesId,
        );
        return series ? `${series?.name} search results` : '';
      }
    },
    path: '/malpracticeReports/:seriesId',
    pathResolver: (state: RootState, match: Params | null): string | undefined => {
      const {
        malpracticeReport: { searchQuery },
      } = state;

      if (match) {
        return `/malpracticeReports/${match.params.seriesId}?${qs.stringify({ searchQuery })}`;
      }
    },
    dataQaId: 'link-priming-category-malpractice-reports-results',
    router: true,
    size: 'medium',
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER],
  },
  {
    title: 'Similarity Index Reports',
    path: '/malpracticeReports/:seriesId/:assessmentCode/:componentNumber/:sectionId/details',
    dataQaId: 'link-priming-category-malpractice-reports-detailed-view',
    router: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER],
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'File processing errors',
    path: '/malpracticeReports/:seriesId/:assessmentCode/:componentNumber/:sectionId/errors',
    dataQaId: 'link-priming-category-malpractice-reports-processing-errors',
    router: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER],
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Submission reports',
    path: '/submissionReports',
    dataQaId: 'link-priming-category-submission-reports',
    router: true,
    size: 'medium',
    canAccess: [
      Roles.SUBMIT_SUPER_ADMIN,
      Roles.SUBMIT_ADMINISTRATOR,
      Roles.SUBMIT_COMPLIANCE,
      Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
    ],
    sideNavRoute: true,
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Replacement files',
    path: '/replacementFiles',
    dataQaId: 'link-priming-category-replacement-files',
    router: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR, Roles.SUBMIT_COMPLIANCE],
    sideNavRoute: true,
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Training reports',
    path: '/trainingReports',
    dataQaId: 'link-priming-category-training-reports',
    router: true,
    size: 'medium',
    canAccess: [
      Roles.SUBMIT_SUPER_ADMIN,
      Roles.SUBMIT_ADMINISTRATOR,
      Roles.SUBMIT_COMPLIANCE,
      Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
    ],
    sideNavRoute: true,
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Manage consortia',
    path: '/manageConsortia',
    dataQaId: 'link-priming-category-consortia-management',
    router: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR],
    sideNavRoute: true,
    availableForBU: [BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Create new consortium',
    path: '/manageConsortia/createNewConsortium',
    dataQaId: 'link-priming-category-manage-consortia-new-consortium',
    router: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR],
    availableForBU: [BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Edit consortium',
    path: '/manageConsortia/editConsortium',
    dataQaId: 'link-priming-category-manage-consortia-edit-consortium',
    router: true,
    size: 'medium',
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR],
    availableForBU: [BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'View and download candidate work',
    path: '/downloadCandidateWork',
    dataQaId: 'link-priming-category-work-downloading',
    router: true,
    size: 'medium',
    canAccess: [
      Roles.SUBMIT_SUPER_ADMIN,
      Roles.SUBMIT_ADMINISTRATOR,
      Roles.SUBMIT_COMPLIANCE,
      Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
    ],
    sideNavRoute: true,
    availableForBU: [BUSINESS_UNITS.CIE.id, BUSINESS_UNITS.OCR.id],
  },
  {
    title: 'Manage moderated sample selections',
    path: '/manageSampleChanges',
    dataQaId: 'link-priming-category-manage-sample-changes',
    router: true,
    size: 'medium',
    sideNavRoute: true,
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR],
    availableForBU: [BUSINESS_UNITS.OCR.id, BUSINESS_UNITS.CIE.id],
  },
  {
    title: 'RM file submission failures',
    path: '/resendFilesToRm',
    dataQaId: 'link-priming-category-rm-submission',
    router: true,
    size: 'medium',
    sideNavRoute: true,
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR],
    availableForBU: [BUSINESS_UNITS.CIE.id],
  },
  {
    title: 'Post results apportionment',
    path: '/postResultsApportionment',
    dataQaId: 'link-priming-category-post-results',
    router: true,
    size: 'medium',
    sideNavRoute: true,
    canAccess: [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR],
    availableForBU: [BUSINESS_UNITS.OCR.id, BUSINESS_UNITS.CIE.id],
    featureFlag: FEATURE_FLAGS.postResults_ff,
  },
];
